import React, { useState } from "react";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../authConfig";
import { useAuth } from "../AuthProvider";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { message, Modal, Form, Input, Spin } from "antd";
import { jwtDecode } from "jwt-decode";
import { useTranslation } from "react-i18next";

const LoginButton = () => {
  const { instance } = useMsal();
  const { setAuth } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [userEmail, setUserEmail] = useState("");
  const [userName, setUserName] = useState("");
  const [isSignupVisible, setIsSignupVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [isDaiichiUser, setIsDaiichiUser] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL;

  const handleLogin = () => {
    setLoading(true);
    instance
      .loginPopup(loginRequest)
      .then(async (response) => {
        console.log("✅ Azure AD Login success!", response);

        const decodedToken = jwtDecode(response.idToken);
        const name = decodedToken.name || "Unknown User";
        const email = decodedToken.preferred_username;

        console.log("📋 User info:", { name, email }); // ✅ Debug log

        setUserName(name);
        setUserEmail(email);

        // ✅ name parametresini geç
        checkSupplier(email, name);
      })
      .catch((error) => {
        console.error("❌ Azure AD login failed", error);
        message.error("Azure AD girişi başarısız.");
        setLoading(false);
      });
  };

  const checkSupplier = async (email, name) => {  // ✅ name parametresi
    try {
      const response = await axios.post(
        `${apiUrl}/api/Auth/CheckSupplier`,
        { email }
      );

      if (response.data.exists) {
        // ✅ name parametresini geç
        await loginExistingUser(email, name);
      } else {
        setLoading(false);
        
        if (email.endsWith("@daiichi.com")) {
          setIsDaiichiUser(true);
          form.setFieldsValue({ role: "Viewer" });
        } else {
          setIsDaiichiUser(false);
          form.setFieldsValue({ role: "Supplier" });
        }

        setIsSignupVisible(true);
      }
    } catch (error) {
      console.error("❌ User check error:", error);
      message.error("Kullanıcı kontrolü başarısız.");
      setLoading(false);
    }
  };

  const loginExistingUser = async (email, name) => {  // ✅ name parametresi
    try {
      console.log("📤 Login request data:", { email, name }); // ✅ Debug log

      const loginResponse = await axios.post(
        `${apiUrl}/api/Auth/AzureAdLogin`,
        { email, name }  // ✅ Parametre kullan
      );

      console.log("✅ Backend response:", loginResponse.data);

      localStorage.setItem("token", loginResponse.data.token);
      localStorage.setItem("refreshToken", loginResponse.data.refreshToken);
      localStorage.setItem("urlToken", loginResponse.data.token);
      localStorage.setItem("user", JSON.stringify(loginResponse.data.user));

      setAuth({
        isAuthenticated: true,
        token: loginResponse.data.token,
        user: loginResponse.data.user
      });

      message.success("Giriş başarılı!");

      const role = loginResponse.data.user.role;
      if (role === "SuperAdmin" || role === "Admin" || role === "Viewer") {
        navigate("/admin-dashboard");
      } else {
        navigate("/main");
      }

    } catch (error) {
      console.error("❌ Login error:", error);
      console.error("❌ Error response:", error.response?.data);
      
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.errors?.join(", ") ||
                          "Giriş başarısız.";
      
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async () => {
    try {
      const values = await form.validateFields();

      const supplierData = {
        supplierId: "00000000-0000-0000-0000-000000000000",
        supplierName: userName,
        supplierCode: `${isDaiichiUser ? 'DAI' : 'SUP'}-${Math.floor(Math.random() * 10000)}`,
        category: values.category || "-",
        address: values.address || "-",
        businessContact: values.contactEmail || userEmail,
        businessEmail: userEmail,
        businessPhone: values.businessPhone || "-",
        logisticsContactName: "",
        logisticsContactEmail: "",
        qualityContactName: "",
        qualityContactEmail: "",
        location: values.location || "-",
        business: isDaiichiUser,
        supplierLevel: "-",
        qualityAuditOverallRating: "-",
        productSafetyRegulatory: "-",
        customerApprovedSupplier: isDaiichiUser,
        supplierPerformance: "-",
        remark: "Registered via Azure AD",
        password: values.password,
        role: form.getFieldValue("role"),
        products: [],
        certificates: []
      };

      setLoading(true);
      console.log("📤 Registration data:", supplierData);

      const response = await axios.post(
        `${apiUrl}/api/Auth/RegisterSupplier`,
        supplierData
      );

      if (response.status === 200) {
        message.success(`Kayıt başarılı! Rolünüz: ${supplierData.role}`);
        setIsSignupVisible(false);

        // ✅ userName state'ini kullan (artık set edilmiş)
        await loginExistingUser(userEmail, userName);
      }
    } catch (error) {
      console.error("❌ Registration error:", error);
      console.error("❌ Error response:", error.response?.data);
      
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.errors?.join(", ") ||
                          "Kayıt başarısız.";
      
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div
        onClick={handleLogin}
        style={{
          textDecoration: 'underline',
          color: '#7fcdfa',
          cursor: 'pointer'
        }}
      >
        {loading ? <Spin /> : t("azure_login")}
      </div>

      <Modal
        title={t("signup")}
        visible={isSignupVisible}
        onCancel={() => setIsSignupVisible(false)}
        onOk={handleSignup}
        confirmLoading={loading}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="role" hidden>
            <Input />
          </Form.Item>

          {!isDaiichiUser && (
            <>
              <Form.Item
                label="Category"
                name="category"
                rules={[{ required: true, message: "This field is required." }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Address"
                name="address"
                rules={[{ required: true, message: "This field is required." }]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Contact Email"
                name="contactEmail"
                rules={[
                  { required: true, message: "This field is required." },
                  { type: "email", message: "Please enter a valid email address!" }
                ]}
              >
                <Input />
              </Form.Item>
              <Form.Item
                label="Business Phone"
                name="businessPhone"
                rules={[{ required: true, message: "This field is required." }]}
              >
                <Input />
              </Form.Item>
              <Form.Item label="Location" name="location">
                <Input />
              </Form.Item>
            </>
          )}

          <Form.Item
            label="Password"
            name="password"
            rules={[
              { required: true, message: "Please input your password!" },
              { min: 8, message: "Password must be at least 8 characters" },
              {
                pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9])/,
                message: "Password must contain uppercase, lowercase, number and special character"
              }
            ]}
          >
            <Input.Password />
          </Form.Item>

          <Form.Item
            label="Confirm Password"
            name="confirmPassword"
            dependencies={["password"]}
            rules={[
              { required: true, message: "Please confirm your password!" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("password") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("The two passwords do not match!"));
                }
              })
            ]}
          >
            <Input.Password />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default LoginButton;
